import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  auth,
  couponsService,
  storesService,
  categoriesService,
  newsletterService,
} from "@/services/supabase";

export const useCouponStore = defineStore("coupons", () => {
  // State
  const coupons = ref([]);
  const stores = ref([]);
  const categories = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const activeCoupons = computed(() => {
    const now = new Date();
    return coupons.value.filter((coupon) =>
      coupon.expiry_date ? new Date(coupon.expiry_date) > now : true,
    );
  });

  const featuredCoupons = computed(() => {
    return coupons.value.filter((coupon) => coupon.is_featured);
  });

  const exclusiveCoupons = computed(() => {
    return coupons.value.filter((coupon) => coupon.is_exclusive);
  });

  const getCouponsByStore = (storeId) => {
    return coupons.value.filter((coupon) => coupon.store_id === storeId);
  };

  const getCouponsByCategory = (categoryId) => {
    return coupons.value.filter((coupon) => coupon.category_id === categoryId);
  };

  // Actions
  const fetchCoupons = async (filters = {}, page = 1, limit = 100) => {
    try {
      loading.value = true;
      error.value = null;
      const { data, error: apiError } = await couponsService.getAll(
        filters,
        page,
        limit,
      );

      if (apiError) {
        throw apiError;
      }

      coupons.value = data || [];
    } catch (err) {
      error.value = err.message || "Failed to fetch coupons";
      console.error("Error fetching coupons:", err);
    } finally {
      loading.value = false;
    }
  };

  const fetchStores = async () => {
    try {
      loading.value = true;
      error.value = null;
      
      console.log("Fetching stores...");
      const { data, error: apiError } = await storesService.getAll();

      if (apiError) {
        console.error("API error fetching stores:", apiError);
        throw apiError;
      }

      if (!data) {
        console.warn("No store data returned from API");
        stores.value = [];
        return;
      }

      console.log(`Successfully fetched ${data.length} stores`);
      
      // Process and validate each store
      const validatedStores = data.map(store => {
        if (!store.logo_url) {
          console.warn(`Store ${store.id} (${store.name}) has no logo_url, using placeholder`);
          store.logo_url = '/images/placeholder.png';
        }
        return store;
      });
      
      stores.value = validatedStores;
    } catch (err) {
      error.value = err.message || "Failed to fetch stores";
      console.error("Error fetching stores:", err);
      stores.value = []; // Ensure we always have an array even on error
    } finally {
      loading.value = false;
    }
  };

  const fetchCategories = async () => {
    try {
      loading.value = true;
      error.value = null;
      const { data, error: apiError } = await categoriesService.getAll();

      if (apiError) {
        throw apiError;
      }

      categories.value = data || [];
    } catch (err) {
      error.value = err.message || "Failed to fetch categories";
      console.error("Error fetching categories:", err);
    } finally {
      loading.value = false;
    }
  };

  const trackCouponClick = async (couponId) => {
    try {
      await couponsService.trackClick(couponId);
    } catch (err) {
      console.error("Error tracking coupon click:", err);
    }
  };

  /**
   * Subscribe user to the newsletter
   * @param {string} email - User email to subscribe
   */
  const subscribeToNewsletter = async (email) => {
    try {
      await newsletterService.subscribe(email);
    } catch (err) {
      console.error("Error subscribing to newsletter:", err);
      throw err;
    }
  };

  return {
    // State
    coupons,
    stores,
    categories,
    loading,
    error,
    
    // Getters
    activeCoupons,
    featuredCoupons,
    exclusiveCoupons,
    getCouponsByStore,
    getCouponsByCategory,

    // Actions
    fetchCoupons,
    fetchStores,
    fetchCategories,
    trackCouponClick,
    subscribeToNewsletter
  };
});

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const adminStatus = ref({
    isAdmin: false,
    isSuperAdmin: false,
    adminData: null
  });

  // Getters
  const isAuthenticated = computed(() => !!user.value);
  
  // Check if user is an admin (exists in admin_users table)
  const isAdmin = computed(() => adminStatus.value.isAdmin);
  
  // Check if user is a super admin
  const isSuperAdmin = computed(() => adminStatus.value.isSuperAdmin);

  // Actions
  const login = async (email, password) => {
    try {
      loading.value = true;
      error.value = null;
      
      console.log("Auth Store: Logging in user", email);

      const { data, error: authError } = await auth.signIn(email, password);

      if (authError) {
        console.error("Auth Store: Login error", authError);
        throw authError;
      }

      if (data?.user) {
        console.log("Auth Store: Login successful, setting user", data.user.id);
        user.value = data.user;
        localStorage.setItem("user", JSON.stringify(data.user));
        
        // Store session for debugging
        if (data.session) {
          localStorage.setItem("session_expires_at", new Date(data.session.expires_at * 1000).toISOString());
        }
        
        // Check if user is an admin by querying the admin_users table
        const { isAdmin: isAdminUser, isSuperAdmin, adminData } = await auth.checkAdminStatus(data.user.id);
        
        console.log("Admin status check:", isAdminUser ? "User is admin" : "User is not admin", 
                    isSuperAdmin ? "User is super admin" : "User is not super admin");
        
        adminStatus.value = {
          isAdmin: isAdminUser,
          isSuperAdmin: isSuperAdmin,
          adminData: adminData
        };
        
        // Store admin status in localStorage for persistence
        localStorage.setItem("adminStatus", JSON.stringify({
          isAdmin: isAdminUser,
          isSuperAdmin: isSuperAdmin
        }));
        
      } else {
        console.error("Auth Store: Login response missing user data");
        error.value = "Login response missing user data";
      }
    } catch (err) {
      error.value = err.message || "Failed to log in";
      console.error("Auth Store: Error during login:", err);
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      loading.value = true;
      error.value = null;

      const { error: authError } = await auth.signOut();

      if (authError) {
        throw authError;
      }

      user.value = null;
      adminStatus.value = { isAdmin: false, isSuperAdmin: false, adminData: null };
      localStorage.removeItem("user");
      localStorage.removeItem("adminStatus");
    } catch (err) {
      error.value = err.message || "Failed to log out";
      console.error("Error during logout:", err);
    } finally {
      loading.value = false;
    }
  };

  const checkAuth = async () => {
    try {
      loading.value = true;

      // Check local storage first for user data
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        user.value = JSON.parse(storedUser);
      }
      
      // Also check for stored admin status
      try {
        const storedAdminStatus = localStorage.getItem("adminStatus");
        if (storedAdminStatus) {
          const parsedAdminStatus = JSON.parse(storedAdminStatus);
          adminStatus.value = {
            isAdmin: parsedAdminStatus.isAdmin === true,
            isSuperAdmin: parsedAdminStatus.isSuperAdmin === true,
            adminData: parsedAdminStatus.adminData || null
          };
          console.log("Loaded admin status from localStorage:", 
                     "isAdmin:", adminStatus.value.isAdmin, 
                     "isSuperAdmin:", adminStatus.value.isSuperAdmin);
        }
      } catch (err) {
        console.error("Error parsing admin status from localStorage:", err);
      }

      // Verify with Supabase
      const { data } = await auth.getSession();
      console.log("Session check result:", data?.session ? "Active session" : "No active session");

      if (data?.session) {
        const { user: userData, error: userError } = await auth.getUser();
        
        if (userError) {
          console.error("Error retrieving user data:", userError);
          throw userError;
        }
        
        if (userData) {
          console.log("User data retrieved successfully");
          user.value = userData;
          localStorage.setItem("user", JSON.stringify(userData));
          
          // For debug
          console.log("User role check:", 
            "Direct role:", userData.role,
            "App metadata role:", userData.app_metadata?.role,
            "User metadata role:", userData.user_metadata?.role
          );
          
          // Check if the user is an admin from the admin_users table
          const { isAdmin: isAdminUser, isSuperAdmin, adminData } = await auth.checkAdminStatus(userData.id);
          
          console.log("Admin status check during checkAuth:", 
                      isAdminUser ? "User is admin" : "User is not admin",
                      isSuperAdmin ? "User is super admin" : "User is not super admin");
          
          adminStatus.value = {
            isAdmin: isAdminUser,
            isSuperAdmin: isSuperAdmin,
            adminData: adminData
          };
          
          // Store admin status in localStorage for persistence
          localStorage.setItem("adminStatus", JSON.stringify({
            isAdmin: isAdminUser,
            isSuperAdmin: isSuperAdmin
          }));
          
        } else {
          console.error("Session exists but user data is empty");
          // Session exists but user data couldn't be fetched
          user.value = null;
          adminStatus.value = { isAdmin: false, isSuperAdmin: false, adminData: null };
          localStorage.removeItem("user");
          localStorage.removeItem("adminStatus");
        }
      } else {
        console.log("No active session found");
        // No active session
        user.value = null;
        adminStatus.value = { isAdmin: false, isSuperAdmin: false, adminData: null };
        localStorage.removeItem("user");
        localStorage.removeItem("adminStatus");
      }
    } catch (err) {
      console.error("Error checking auth:", err);
      user.value = null;
      localStorage.removeItem("user");
    } finally {
      loading.value = false;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      loading.value = true;
      error.value = null;

      // Call Supabase auth resetPassword
      const { error: resetError } = await auth.resetPassword(email);

      if (resetError) {
        throw resetError;
      }

      return { success: true };
    } catch (err) {
      error.value = err.message || "Failed to send password reset email";
      console.error("Error sending password reset:", err);
      return { error: err };
    } finally {
      loading.value = false;
    }
  };
  
  // Explicitly verify admin status directly from database
  const verifyAdminStatus = async () => {
    try {
      if (!user.value?.id) {
        console.log("Cannot verify admin status: No user ID available");
        adminStatus.value = { isAdmin: false, isSuperAdmin: false, adminData: null };
        return false;
      }
      
      console.log("Explicitly verifying admin status for user:", user.value.id);
      const { isAdmin: isAdminUser, isSuperAdmin, adminData } = await auth.checkAdminStatus(user.value.id);
      
      console.log("Admin verification result:", 
                 isAdminUser ? "User is admin" : "User is not admin",
                 isSuperAdmin ? "User is super admin" : "User is not super admin");
                 
      adminStatus.value = {
        isAdmin: isAdminUser,
        isSuperAdmin: isSuperAdmin,
        adminData: adminData
      };
      
      // Update localStorage
      localStorage.setItem("adminStatus", JSON.stringify({
        isAdmin: isAdminUser,
        isSuperAdmin: isSuperAdmin
      }));
      
      return isAdminUser;
    } catch (err) {
      console.error("Error verifying admin status:", err);
      adminStatus.value = { isAdmin: false, isSuperAdmin: false, adminData: null };
      return false;
    }
  };
  
  // Update user profile
  const updateProfile = async (userData) => {
    try {
      loading.value = true;
      error.value = null;
      
      // Call Supabase auth update
      const { data, error: updateError } = await auth.updateUser(userData);
      
      if (updateError) {
        throw updateError;
      }
      
      if (data?.user) {
        user.value = data.user;
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      return { success: true };
    } catch (err) {
      error.value = err.message || "Failed to update profile";
      console.error("Error updating profile:", err);
      return { error: err };
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    user,
    loading,
    error,
    adminStatus,

    // Getters
    isAuthenticated,
    isAdmin,
    isSuperAdmin,

    // Actions
    login,
    logout,
    checkAuth,
    resetPassword,
    updateProfile,
    verifyAdminStatus
  };
});
